<script setup lang="ts">
import type { Agent } from '~/core/entities/agent/Agent';
import type {
	CondoMonthlyCosts,
	CondoPriceRange
} from '~/core/entities/condo/Condo';

import { validateRange } from '@/utils';
import { formatCurrency, grammarInflection } from '~~/utils';
import { CONDO_DETAILS } from '~~/utils/symbols';

const condominium = inject(CONDO_DETAILS);

const agents = computed((): Agent[] => {
	if (!condominium?.value) return [];
	return (condominium.value.agents || []).slice(0, 4);
});

const numberOfAgents = computed<number>(
	() => condominium?.value?.agents?.length || 0
);
const agentsLabel = computed<string>(() => {
	if (!numberOfAgents.value) return 'Fale com um corretor do condomínio';

	const label = grammarInflection(
		numberOfAgents.value,
		'corretor disponível no condomínio',
		'corretores disponíveis no condomínio'
	);

	return `${numberOfAgents.value} ${label}`;
});

const priceRange = computed<CondoPriceRange | null>(
	() => condominium?.value?.propertyStatistics?.priceRange || null
);

const monthlyCosts = computed<Record<string, CondoPriceRange> | null>(() => {
	const costs = condominium?.value?.propertyStatistics?.monthlyCosts;
	const validCosts: Record<string, CondoPriceRange> = {};

	for (const [key, cost] of Object.entries(costs || {})) {
		if (validateRange(cost)) {
			validCosts[key] = cost;
		}
	}

	return validCosts;
});

const hasPriceRange = computed<boolean>(() => {
	return validateRange(priceRange.value);
});

const hasMonthlyCosts = computed<boolean>(() => {
	const condoFee = monthlyCosts.value?.condoFee || null;
	const tax = monthlyCosts.value?.tax || null;

	return validateRange(condoFee) || validateRange(tax);
});

const montlyCostsByLabel: Record<string, string> = {
	condoFee: 'Taxa de condomínio',
	tax: 'IPTU mensal'
};

const macroregionsByState: Record<string, 'São Paulo' | 'Curitiba'> = {
	SP: 'São Paulo',
	PR: 'Curitiba'
};

const condoMacroregion = computed<'São Paulo' | 'Curitiba'>(() => {
	const condoState = condominium?.value?.condo?.state || 'SP';

	return macroregionsByState[condoState] || 'São Paulo';
});

function getFormattedCurrency(value: number) {
	return formatCurrency(value).replace(',', '.');
}
</script>

<template>
	<div class="flex h-auto w-full flex-col gap-8 bg-secondary p-6 md:p-8">
		<div v-if="hasPriceRange || hasMonthlyCosts" class="flex flex-col gap-4">
			<div v-if="!!priceRange" class="flex flex-col">
				<p class="text-sm font-normal leading-[150%] text-primary-light-3">
					Faixa de preço dos imóveis
				</p>
				<div
					class="flex items-center gap-[6px] font-matter-sq text-xl font-normal leading-[150%] tracking-[-0.4px] text-primary md:text-2xl md:leading-[36px]"
				>
					<p v-if="priceRange.min">
						{{ getFormattedCurrency(priceRange.min) }}
					</p>

					<IconMaterialSymbolsArrowRange
						v-if="priceRange.min && priceRange.max"
						class="size-4 min-h-4 text-primary-light-1"
					/>

					<p v-if="priceRange.max">
						{{ getFormattedCurrency(priceRange.max) }}
					</p>
				</div>
			</div>

			<div
				v-if="!!hasMonthlyCosts"
				class="flex flex-col items-center gap-2 self-stretch"
			>
				<div
					class="flex flex-col items-center divide-y divide-secondary-dark-1 self-stretch"
				>
					<div
						v-for="(cost, key) in monthlyCosts"
						:key="`monthly-cost-${key}`"
						class="flex items-center justify-between self-stretch text-nowrap py-2 first:pt-0 last:pb-0"
					>
						<p
							class="min-h-[21px] text-xs font-normal leading-[150%] text-primary-light-3 md:text-sm md:leading-[21px]"
						>
							{{ montlyCostsByLabel[key] }}
						</p>
						<div class="flex items-center gap-1">
							<p
								v-if="cost.min"
								class="text-sm font-normal leading-[150%] tracking-tighter text-primary-light-3"
							>
								{{ getFormattedCurrency(cost.min) }}
							</p>

							<IconMaterialSymbolsArrowRange
								v-if="cost.min && cost.max"
								class="size-3 min-h-3 text-primary-light-1"
							/>

							<p
								class="text-sm font-normal leading-[150%] tracking-tighter text-primary-light-3"
							>
								{{ getFormattedCurrency(cost.max) }}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div
			v-if="!hasPriceRange && !hasMonthlyCosts && !numberOfAgents"
			class="flex flex-col gap-2"
		>
			<div class="flex flex-col items-start gap-2">
				<IconMaterialSymbolsLightSupportAgentOutlineSharp
					class="size-8 min-h-8 text-primary"
				/>
				<p
					class="text-base font-medium leading-[150%] text-primary md:text-[18px]"
				>
					Conheça o {{ condominium?.condo.name }}
				</p>
			</div>

			<p
				class="text-sm font-medium leading-[150%] text-primary-light-1 lg:text-base"
			>
				Um dos empreendimentos mais exclusivos de {{ condoMacroregion }}.
				Descubra unidades disponíveis, valores e diferenciais únicos.
			</p>
		</div>

		<div
			v-if="numberOfAgents"
			class="flex flex-col items-start gap-2 self-stretch sm:flex-row sm:items-center lg:gap-3"
		>
			<BrokerAvatarSequence
				:agents="agents"
				avatar-class="min-w-9 size-9 md:size-11 border-secondary border [&:not(:first-child)]:-ml-6"
			/>

			<p class="max-w-56 text-base font-medium leading-[150%] text-primary">
				{{ agentsLabel }}
			</p>
		</div>

		<div>
			<slot />
		</div>
	</div>
</template>
